import { createSteeringURL } from '@/api'
import { steeringAuthInit, SteeringUser, useAuthStore, useHasUserPermission } from '@/stores/auth'
import { useEpisodeStore } from '@/stores/episodes'
import { useMediaCascade } from '@/stores/media'
import { useShowStore } from '@/stores/shows'
import { Episode, KeysFrom, Schedule, TimeSlot, TimeSlotMoveData } from '@/types'
import { aggregateWithIdsParameter, APIConsume, useStoreHelpers } from '@/util/api'
import { ensureError } from '@rokoli/bnb/common'
import {
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  BaseRequestOptions,
  createExtendableAPI,
  ExtendableAPI,
  useErrorList,
} from '@rokoli/bnb/drf'
import { useAsyncState, useNow } from '@vueuse/core'
import { formatISO } from 'date-fns'
import { sortBy } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, ref, toRef, toValue } from 'vue'

type ReadonlyAttrs = KeysFrom<
  TimeSlot,
  'id' | 'start' | 'end' | 'repetitionOfId' | 'scheduleId' | 'showId'
>
type TimeSlotUpdateData = Omit<TimeSlot, ReadonlyAttrs>
type TimeSlotPartialUpdateData = Partial<TimeSlotUpdateData>

export const useTimeSlotStore = defineStore('timeslots', () => {
  const endpoint = createSteeringURL.prefix('timeslots')
  const { api, base } = createExtendableAPI<TimeSlot>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })

  useEpisodeUpdateCascadeBehaviour(api)

  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APIConsume(listOperations.listIsolated),
    ...APIUpdate<TimeSlot, TimeSlotUpdateData, TimeSlotPartialUpdateData>(api),
    ...APIRemove(api),
    ...APIMove(api),
    ...useStoreHelpers({ ...base, ...listOperations, ...retrieveOperations }),
  }
})

export function APIMove<T extends TimeSlot>(api: ExtendableAPI<T>) {
  async function move(
    id: T['id'],
    data: Omit<TimeSlotMoveData, 'newStart' | 'newEnd'> & {
      newStart?: Date | string | null | undefined
      newEnd?: Date | string | null | undefined
    },
    options: BaseRequestOptions | undefined = undefined,
  ) {
    if (data.newStart instanceof Date) {
      data.newStart = formatISO(data.newStart)
    }
    if (data.newEnd instanceof Date) {
      data.newEnd = formatISO(data.newEnd)
    }
    const res = await fetch(
      api.createRequest(api.endpoint(id, 'move'), options?.requestInit, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    )
    await api.maybeThrowResponse(res)
    const ts = (await res.json()) as T
    api.itemMap.value.set(ts.id, ts)
    return ts
  }

  return { move }
}

function useEpisodeUpdateCascadeBehaviour(api: ExtendableAPI<TimeSlot>) {
  async function fetchUpdates(episodeIds: (TimeSlot['episodeId'] | undefined)[]) {
    const episodeStore = useEpisodeStore()
    const timeslotStore = useTimeSlotStore()
    const episodeResults = await episodeStore.retrieveMultiple(
      episodeIds.filter((id) => Boolean(id)) as number[],
    )
    await timeslotStore.retrieveMultiple(
      episodeResults
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<Episode>).value.timeslotIds),
    )
  }

  api.events.addEventListener('set', (event) => {
    const oldEpisodeId = api.itemMap.value.get(event.detail.id)?.episodeId
    if (oldEpisodeId === undefined) return
    const newEpisodeId = event.detail.object.episodeId
    if (oldEpisodeId !== newEpisodeId)
      setTimeout(() => void fetchUpdates([newEpisodeId, oldEpisodeId]), 0)
  })
}

export function useTimeslotMediaCascade(timeslot: MaybeRefOrGetter<TimeSlot | null>) {
  return useMediaCascade(() => toValue(timeslot)?.resolvedMedia ?? null, ['self', 'episode'])
}

export function createTimeslotDomId(id: TimeSlot['id']) {
  return `ts-${id}`
}

export function useIsTimeslotOwner(id: MaybeRefOrGetter<TimeSlot['id'] | null>) {
  const authStore = useAuthStore()
  const showStore = useShowStore()
  const timeslotStore = useTimeSlotStore()
  const { obj: timeslot } = timeslotStore.use(id)
  const { obj: show } = showStore.use(() => timeslot?.value?.showId)
  const canEditAll = useHasUserPermission(['program.update_timeslot'])

  return computed(
    () =>
      canEditAll.value ||
      (show.value?.ownerIds?.includes?.(authStore?.steeringUser?.id as SteeringUser['id']) ??
        false),
  )
}

export function useRepetitionStartTimeSlot(
  repSchedule: MaybeRefOrGetter<Schedule | null | undefined>,
) {
  const timeslotStore = useTimeSlotStore()
  const repetition = toRef(repSchedule)
  const now = useNow({ interval: 60 * 1000 })
  const today = computed(() => formatISO(now.value, { representation: 'date' }))

  const type = ref<'first' | 'recent' | 'selection'>('first')
  const {
    state: pastTimeslots,
    error: pastTimeslotsError,
    isLoading: pastTimeslotsIsLoading,
  } = useAsyncState(async () => {
    if (!repetition.value) return []
    return timeslotStore.consume({
      noCache: true,
      query: new URLSearchParams({
        scheduleIds: repetition.value.id.toString(),
        order: '-end',
        endsBefore: today.value,
        pickFields: 'id,start',
      }),
    })
  }, [])
  const pastTimeslotsErrors = useErrorList(() =>
    pastTimeslotsError.value ? ensureError(pastTimeslotsError) : null,
  )
  const firstTimeSlotId = computed(() => pastTimeslots.value.at(-1)?.id)
  const mostRecentTimeSlotId = computed(() => pastTimeslots.value[0]?.id)
  const timeslotSelectionId = ref<TimeSlot['id'] | undefined>()
  const repetitionStartId = computed(() => {
    if (type.value === 'selection') return timeslotSelectionId.value
    if (type.value === 'first') return firstTimeSlotId.value
    if (type.value === 'recent') return mostRecentTimeSlotId.value
    return undefined
  })
  const {
    obj: timeslot,
    isLoading: isLoadingTimeslot,
    error: timeslotError,
  } = timeslotStore.use(repetitionStartId)

  return {
    timeslot,
    isLoadingTimeslot,
    timeslotError,
    type,
    timeslotSelectionId,
    pastTimeslots,
    pastTimeslotsErrors,
    pastTimeslotsIsLoading,
  }
}

export function useFirstEpisodeBroadcast(
  episode: MaybeRefOrGetter<Episode | Episode['id'] | null | undefined>,
) {
  const timeslotStore = useTimeSlotStore()
  const episodeStore = useEpisodeStore()
  const { obj: existingEpisode, isLoading: isLoadingEpisode } = episodeStore.use(() => {
    const v = toValue(episode) ?? null
    return v !== null && typeof v === 'number' ? v : (v?.id ?? null)
  })
  const { objects: existingEpisodeTimeslots, isLoading: isLoadingTimeslots } =
    timeslotStore.useList(() => existingEpisode.value?.timeslotIds ?? [])
  const firstEpisodeBroadcast = computed(() => {
    const orderedTimeslots = sortBy(existingEpisodeTimeslots.value, ['start'])
    return orderedTimeslots[0] ?? null
  })
  const isLoading = computed(() => isLoadingEpisode.value || isLoadingTimeslots.value)
  return { firstBroadcast: firstEpisodeBroadcast, isLoading }
}
